import React, { useEffect, useState } from "react";

import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase/firebase";

import "./GalleryAdmin.css";

// ======================================================
// CATEGORIES
// ======================================================

const categories = {
  Photography: [
    "Weddings",
    "Islamic Weddings",
    "Engagements",
    "Bridal Sessions",
    "Women Events",
    "Family & Friends",
  ],

  Rental: [
    "Bridal Robes",
    "Bridesmaids Robe",
    "Accessories",
  ],
};

// ======================================================
// SETTINGS
// ======================================================

const MAX_IMAGES = 7;

const MAX_WIDTH = 1000;
const MAX_HEIGHT = 1000;

const IMAGE_QUALITY = 0.65;

// ======================================================
// COMPRESS IMAGE
// ======================================================

const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Resize
        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          const ratio = Math.min(
            MAX_WIDTH / width,
            MAX_HEIGHT / height
          );

          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        // Canvas
        const canvas = document.createElement("canvas");

        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext("2d");

        context.drawImage(
          img,
          0,
          0,
          width,
          height
        );

        // Convert to JPEG
        const compressedImage = canvas.toDataURL(
          "image/jpeg",
          IMAGE_QUALITY
        );

        resolve(compressedImage);
      };

      img.onerror = () => {
        reject(
          new Error("Could not load image.")
        );
      };

      img.src = event.target.result;
    };

    reader.onerror = () => {
      reject(
        new Error("Could not read image.")
      );
    };

    reader.readAsDataURL(file);
  });
};

// ======================================================
// GALLERY ADMIN
// ======================================================

function GalleryAdmin() {
  // ====================================================
  // STATE
  // ====================================================

  const [type, setType] = useState("Photography");

  const [category, setCategory] = useState("Weddings");

  const [title, setTitle] = useState("");

  const [description, setDescription] = useState("");

  const [selectedFiles, setSelectedFiles] = useState([]);

  const [previewImages, setPreviewImages] = useState([]);

  const [photos, setPhotos] = useState([]);

  const [loading, setLoading] = useState(false);

  const [loadingGallery, setLoadingGallery] = useState(true);

  // ====================================================
  // GET GALLERY
  // ====================================================

  const fetchPhotos = async () => {
    try {
      setLoadingGallery(true);

      const snapshot = await getDocs(
        collection(db, "gallery")
      );

      const galleryData = snapshot.docs.map(
        (item) => ({
          id: item.id,
          ...item.data(),
        })
      );

      setPhotos(galleryData);
    } catch (error) {
      console.error(
        "FIRESTORE GET ERROR:",
        error
      );

      alert(
        `Could not load gallery.\n\n${error.message}`
      );
    } finally {
      setLoadingGallery(false);
    }
  };

  // ====================================================
  // LOAD GALLERY
  // ====================================================

  useEffect(() => {
    fetchPhotos();
  }, []);

  // ====================================================
  // TYPE CHANGE
  // ====================================================

  const handleTypeChange = (event) => {
    const selectedType = event.target.value;

    setType(selectedType);

    setCategory(
      categories[selectedType][0]
    );
  };

  // ====================================================
  // SELECT FILES
  // ====================================================

  const handleFileChange = (event) => {
    const files = Array.from(
      event.target.files || []
    );

    if (files.length === 0) {
      return;
    }

    // Images only
    const imageFiles = files.filter(
      (file) =>
        file.type.startsWith("image/")
    );

    if (imageFiles.length === 0) {
      alert(
        "Please select image files only."
      );

      return;
    }

    // Maximum 7
    if (
      selectedFiles.length +
        imageFiles.length >
      MAX_IMAGES
    ) {
      alert(
        `You can upload a maximum of ${MAX_IMAGES} photos.`
      );

      event.target.value = "";

      return;
    }

    // Save files
    setSelectedFiles(
      (previousFiles) => [
        ...previousFiles,
        ...imageFiles,
      ]
    );

    // Create previews
    const newPreviews = imageFiles.map(
      (file) =>
        URL.createObjectURL(file)
    );

    setPreviewImages(
      (previousImages) => [
        ...previousImages,
        ...newPreviews,
      ]
    );

    // Allow selecting same file again
    event.target.value = "";
  };

  // ====================================================
  // REMOVE SELECTED PHOTO
  // ====================================================

  const removeSelectedFile = (index) => {
    if (previewImages[index]) {
      URL.revokeObjectURL(
        previewImages[index]
      );
    }

    setSelectedFiles(
      (previousFiles) =>
        previousFiles.filter(
          (_, i) => i !== index
        )
    );

    setPreviewImages(
      (previousImages) =>
        previousImages.filter(
          (_, i) => i !== index
        )
    );
  };

  // ====================================================
  // ADD COLLECTION
  // ====================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Title
    if (!title.trim()) {
      alert(
        "Please enter a title."
      );

      return;
    }

    // Images
    if (selectedFiles.length === 0) {
      alert(
        "Please select at least one photo."
      );

      return;
    }

    // Maximum
    if (
      selectedFiles.length >
      MAX_IMAGES
    ) {
      alert(
        `Maximum ${MAX_IMAGES} photos allowed.`
      );

      return;
    }

    try {
      setLoading(true);

      // ==================================================
      // COMPRESS IMAGES
      // ==================================================

      const convertedImages = [];

      for (const file of selectedFiles) {
        const compressedImage =
          await compressImage(file);

        convertedImages.push(
          compressedImage
        );
      }

      // ==================================================
      // SAVE TO FIRESTORE
      // ==================================================

      await addDoc(
        collection(db, "gallery"),
        {
          title: title.trim(),

          description:
            description.trim(),

          type,

          category,

          // All images
          images: convertedImages,

          // First image for cards
          image: convertedImages[0],

          // Number of images
          imageCount:
            convertedImages.length,

          createdAt:
            serverTimestamp(),
        }
      );

      // ==================================================
      // CLEAN PREVIEWS
      // ==================================================

      previewImages.forEach(
        (url) => {
          URL.revokeObjectURL(url);
        }
      );

      // ==================================================
      // RESET
      // ==================================================

      setTitle("");

      setDescription("");

      setSelectedFiles([]);

      setPreviewImages([]);

      // ==================================================
      // REFRESH
      // ==================================================

      await fetchPhotos();

      alert(
        "Collection added successfully ✨"
      );
    } catch (error) {
      console.error(
        "FIRESTORE ADD ERROR:",
        error
      );

      // Firestore document too large
      if (
        error.message &&
        error.message
          .toLowerCase()
          .includes("maximum")
      ) {
        alert(
          "The images are too large. Please choose smaller photos."
        );
      } else {
        alert(
          `Firebase Error:\n\n${error.message}`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // ====================================================
  // DELETE COLLECTION
  // ====================================================

  const handleDelete = async (id) => {
    const confirmed =
      window.confirm(
        "Are you sure you want to delete this collection?"
      );

    if (!confirmed) {
      return;
    }

    try {
      await deleteDoc(
        doc(
          db,
          "gallery",
          id
        )
      );

      setPhotos(
        (previousPhotos) =>
          previousPhotos.filter(
            (photo) =>
              photo.id !== id
          )
      );

      alert(
        "Collection deleted successfully."
      );
    } catch (error) {
      console.error(
        "FIRESTORE DELETE ERROR:",
        error
      );

      alert(
        `Delete Error:\n\n${error.message}`
      );
    }
  };

  // ====================================================
  // RENDER
  // ====================================================

  return (
    <main className="gallery-admin">

      {/* ==================================================
          HERO
      ================================================== */}

      <section className="admin-hero">

        <span className="admin-eyebrow">
          PRIVATE STUDIO
        </span>

        <h1>
          Gallery Management
        </h1>

        <p>
          Create beautiful photography
          collections.
        </p>

      </section>

      {/* ==================================================
          ADD COLLECTION
      ================================================== */}

      <section className="gallery-panel">

        <div className="panel-heading">

          <div>

            <span>
              NEW COLLECTION
            </span>

            <h2>
              Add Gallery Collection
            </h2>

          </div>

          <div className="panel-number">
            01
          </div>

        </div>

        <form
          onSubmit={handleSubmit}
        >

          {/* TITLE */}

          <div className="field">

            <label>
              Title
            </label>

            <input
              type="text"
              placeholder="Example: Mr & Mrs Ahmad"
              value={title}
              onChange={(event) =>
                setTitle(
                  event.target.value
                )
              }
            />

          </div>

          {/* TYPE */}

          <div className="field">

            <label>
              Collection
            </label>

            <select
              value={type}
              onChange={
                handleTypeChange
              }
            >

              <option value="Photography">
                Photography
              </option>

              <option value="Rental">
                Rental
              </option>

            </select>

          </div>

          {/* CATEGORY */}

          <div className="field">

            <label>
              Category
            </label>

            <select
              value={category}
              onChange={(event) =>
                setCategory(
                  event.target.value
                )
              }
            >

              {categories[type].map(
                (item) => (
                  <option
                    key={item}
                    value={item}
                  >
                    {item}
                  </option>
                )
              )}

            </select>

          </div>

          {/* DESCRIPTION */}

          <div className="field">

            <label>
              Description
            </label>

            <textarea
              placeholder="Write a short description..."
              value={description}
              onChange={(event) =>
                setDescription(
                  event.target.value
                )
              }
            />

          </div>

          {/* PHOTOS */}

          <div className="field">

            <label>
              Photos
            </label>

            <input
              type="file"
              accept="image/*"
              multiple
              onChange={
                handleFileChange
              }
              disabled={
                selectedFiles.length >=
                MAX_IMAGES
              }
            />

            <p className="photo-help">
              Select up to {MAX_IMAGES} photos.
              Images will be compressed before
              saving.
            </p>

          </div>

          {/* PREVIEWS */}

          {previewImages.length > 0 && (

            <div className="admin-image-preview">

              {previewImages.map(
                (image, index) => (

                  <div
                    className="preview-image-wrapper"
                    key={`${image}-${index}`}
                  >

                    <img
                      src={image}
                      alt={`Preview ${
                        index + 1
                      }`}
                    />

                    <button
                      type="button"
                      className="remove-preview"
                      onClick={() =>
                        removeSelectedFile(
                          index
                        )
                      }
                      disabled={loading}
                    >
                      ×
                    </button>

                    <span>
                      {index + 1}
                    </span>

                  </div>

                )
              )}

            </div>

          )}

          {/* COUNT */}

          {selectedFiles.length > 0 && (

            <div className="selected-count">

              {selectedFiles.length} /{" "}
              {MAX_IMAGES} photos selected

            </div>

          )}

          {/* BUTTON */}

          <button
            className="add-button"
            type="submit"
            disabled={
              loading ||
              selectedFiles.length === 0
            }
          >

            {loading
              ? "Compressing & Saving..."
              : "Add Collection"}

          </button>

        </form>

      </section>

      {/* ==================================================
          GALLERY
      ================================================== */}

      <section className="gallery-section">

        <div className="gallery-heading">

          <div>

            <span>
              YOUR COLLECTION
            </span>

            <h2>
              Gallery
            </h2>

          </div>

          <span className="photo-count">
            {photos.length} COLLECTIONS
          </span>

        </div>

        {/* LOADING */}

        {loadingGallery ? (

          <div className="empty-gallery">
            Loading gallery...
          </div>

        ) : photos.length === 0 ? (

          <div className="empty-gallery">

            <span>
              ✦
            </span>

            <p>
              Your gallery is waiting
              for its first story.
            </p>

          </div>

        ) : (

          <div className="gallery-grid">

            {photos.map(
              (photo) => (

                <article
                  className="gallery-card"
                  key={photo.id}
                >

                  {/* IMAGE */}

                  <div
                    className="gallery-card-image"
                  >

                    {photo.image ? (

                      <img
                        src={photo.image}
                        alt={photo.title}
                        loading="lazy"
                      />

                    ) : (

                      <div>
                        ✦
                      </div>

                    )}

                    <span
                      className="image-count"
                    >
                      {photo.imageCount ||
                        0}{" "}
                      photos
                    </span>

                  </div>

                  {/* INFO */}

                  <div
                    className="gallery-card-info"
                  >

                    <span>
                      {photo.type}
                    </span>

                    <h3>
                      {photo.title}
                    </h3>

                    <p>
                      {photo.category}
                    </p>

                    {photo.description && (
                      <small>
                        {photo.description}
                      </small>
                    )}

                    <button
                      type="button"
                      className="delete-button-text"
                      onClick={() =>
                        handleDelete(
                          photo.id
                        )
                      }
                    >
                      Delete
                    </button>

                  </div>

                </article>

              )
            )}

          </div>

        )}

      </section>

    </main>
  );
}

export default GalleryAdmin;